import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import { getWebPageSchema, getBreadcrumbSchema } from '@/utils/structuredData';

const categoryImages: Record<string, string> = {
  'risk-warning': 'https://readdy.ai/api/search-image?query=Abstract%20warning%20concept%20with%20amber%20and%20warm%20tones%2C%20geometric%20alert%20symbols%2C%20modern%20minimalist%20design%2C%20professional%20security%20atmosphere%2C%20soft%20gradients%2C%20no%20text%2C%20clean%20background&width=1920&height=600&seq=cat-risk&orientation=landscape',
  'healthy-alternative': 'https://readdy.ai/api/search-image?query=Abstract%20fresh%20and%20vibrant%20design%20with%20emerald%20green%20and%20soft%20cream%20tones%2C%20flowing%20organic%20shapes%2C%20healthy%20lifestyle%20concept%2C%20modern%20minimalist%2C%20clean%20background%2C%20no%20text&width=1920&height=600&seq=cat-healthy&orientation=landscape',
  'legal-compliance': 'https://readdy.ai/api/search-image?query=Abstract%20professional%20design%20with%20warm%20taupe%20and%20neutral%20tones%2C%20geometric%20patterns%2C%20legal%20and%20compliance%20concept%2C%20modern%20minimalist%2C%20clean%20background%2C%20no%20text&width=1920&height=600&seq=cat-legal&orientation=landscape',
  'youth-protection': 'https://readdy.ai/api/search-image?query=Abstract%20warm%20and%20protective%20design%20with%20soft%20amber%20and%20cream%20tones%2C%20shield%20and%20family%20concept%2C%20modern%20minimalist%2C%20clean%20background%2C%20no%20text%2C%20caring%20atmosphere&width=1920&height=600&seq=cat-youth&orientation=landscape',
};

const articleImages: Record<string, string> = {
  'qiezi-video-legal-risks': 'https://readdy.ai/api/search-image?query=Chinese%20legal%20documents%20and%20gavel%20on%20dark%20wooden%20desk%20professional%20legal%20atmosphere%20warning%20tone%20Chinese%20law%20theme%20warm%20amber%20tones%20editorial%20photography&width=800&height=500&seq=cat-article-1&orientation=landscape',
  'teenager-protection-guide': 'https://readdy.ai/api/search-image?query=Warm%20family%20scene%20with%20parent%20and%20teenager%20using%20tablet%20together%20caring%20atmosphere%20digital%20safety%20education%20soft%20natural%20lighting%20editorial%20photography&width=800&height=500&seq=cat-article-2&orientation=landscape',
  'legal-video-platforms-guide': 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20Chinese%20video%20platform%20app%20icons%20on%20bright%20white%20desk%20minimalist%20tech%20scene%20warm%20lighting%20editorial%20photography&width=800&height=500&seq=cat-article-3&orientation=landscape',
  'internet-content-law-2026': 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20law%20books%20and%20documents%20on%20clean%20desk%20natural%20light%20trustworthy%20atmosphere%20warm%20neutral%20tones%20editorial%20photography&width=800&height=500&seq=cat-article-4&orientation=landscape',
  'privacy-data-protection': 'https://readdy.ai/api/search-image?query=Dark%20smartphone%20silhouette%20with%20glowing%20warning%20icons%20and%20lock%20symbols%20cybersecurity%20danger%20concept%20dramatic%20lighting%20amber%20accents%20editorial&width=800&height=500&seq=cat-article-5&orientation=landscape',
  'healthy-digital-habits': 'https://readdy.ai/api/search-image?query=Calm%20person%20reading%20on%20tablet%20in%20cozy%20modern%20nook%20with%20plants%20warm%20lighting%20healthy%20digital%20lifestyle%20soft%20tones%20editorial%20photography&width=800&height=500&seq=cat-article-6&orientation=landscape',
  'reporting-channels-guide': 'https://readdy.ai/api/search-image?query=Professional%20hand%20holding%20smartphone%20showing%20reporting%20interface%20clean%20modern%20UI%20bright%20office%20desk%20trustworthy%20atmosphere%20warm%20lighting%20editorial&width=800&height=500&seq=cat-article-7&orientation=landscape',
  'youth-mental-health': 'https://readdy.ai/api/search-image?query=Brain%20model%20and%20psychology%20books%20on%20warm%20wooden%20desk%20with%20gentle%20sunlight%20mental%20health%20education%20calming%20tones%20editorial%20photography&width=800&height=500&seq=cat-article-8&orientation=landscape',
};

const categoryNameMap: Record<string, string> = {
  'risk-warning': '风险警示',
  'healthy-alternative': '健康替代',
  'legal-compliance': '法律合规',
  'youth-protection': '青少年保护',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'risk-warning');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  if (!currentCategory) {
    return (
      <Layout>
        <div className="pt-24 pb-16 text-center">
          <h2 className="font-heading text-2xl text-foreground-900">分类未找到</h2>
        </div>
      </Layout>
    );
  }

  const pageTitle = `${currentCategory.name} - ${siteInfo.fullName}`;
  const pageDesc = currentCategory.description;
  const canonicalUrl = `${siteInfo.url}/category/${activeCategory}`;

  const jsonLd = [
    getWebPageSchema(pageTitle, pageDesc, canonicalUrl),
    getBreadcrumbSchema([
      { name: '首页', item: siteInfo.url },
      { name: currentCategory.name, item: canonicalUrl },
    ]),
  ];

  return (
    <Layout>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory.name},不良视频危害,正规短视频平台,健康娱乐内容,青少年网络安全`}
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />

      <div className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
        <img
          src={categoryImages[activeCategory] || ''}
          alt={currentCategory.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-6 pt-16">
            <div className="max-w-4xl">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-background-50/80 text-sm md:text-base max-w-xl">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: currentCategory.name },
        ]}
      />

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-6 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">{cat.articleCount}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-8 md:py-12 bg-background-50">
        <div className="w-full px-4 md:px-6">
          {categoryArticles.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-file-list-3-line text-4xl text-foreground-300 mb-3"></i>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={articleImages[article.slug] || ''}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 group-hover:text-accent-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-foreground-500 text-xs">
                      <span>{article.author}</span>
                      <div className="flex items-center gap-3">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}